import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ShieldCheck, Check } from "lucide-react";

const PLEDGE_ITEMS = [
  "Provide a high-quality Welcome Video for their squad.",
  "Review the 'Red Light' momentum alerts within 24 hours.",
  "Maintain an active presence in the team communication channel.",
];

export default function LeaderPledge() {
  const { user, updateUser } = useAuth();
  const [accepted, setAccepted] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setAccepted(!!user?.pledge_accepted);
  }, [user]);

  const accept = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/leader/accept-pledge");
      setAccepted(true);
      updateUser({ pledge_accepted: true, pledge_accepted_at: data.pledge_accepted_at });
      toast.success("Pledge accepted. The 80% Guarantee is active.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not accept pledge");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="glass-strong rounded-sm p-6 sm:p-8" data-testid="leader-pledge-section">
      <div className="flex items-start gap-5">
        <div className="hidden sm:flex w-12 h-12 rounded-sm border border-gold/40 items-center justify-center shrink-0">
          <ShieldCheck size={20} className="text-gold" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="hud-label mb-2">80% Success Guarantee</div>
          <h2 className="font-display text-2xl sm:text-3xl text-white font-light mb-3">
            The <span className="text-gold italic">Leader&apos;s Pledge</span>
          </h2>
          <p className="text-sm text-slate-400 mb-5">
            To maintain the 80% Success Guarantee, the Leader agrees to:
          </p>

          <ul className="space-y-2.5 mb-6" data-testid="pledge-items-list">
            {PLEDGE_ITEMS.map((item, i) => (
              <li key={i} className="flex items-start gap-3 text-sm text-slate-300">
                <span className="text-gold font-mono-aviation text-xs mt-1 shrink-0">0{i + 1}</span>
                <span>{item}</span>
              </li>
            ))}
          </ul>

          {accepted ? (
            <div
              className="inline-flex items-center gap-2 px-5 py-3 rounded-sm bg-[#34D399]/10 border border-[#34D399]/40 text-[#34D399] text-sm font-medium"
              data-testid="pledge-accepted-badge"
            >
              <Check size={16} />
              Pledge Accepted ✓
            </div>
          ) : (
            <button
              onClick={accept}
              disabled={loading}
              data-testid="pledge-accept-button"
              className="btn-gold px-6 py-3 rounded-sm flex items-center gap-2 disabled:opacity-60"
            >
              <ShieldCheck size={16} />
              {loading ? "Recording..." : "I Accept the Leader's Pledge"}
            </button>
          )}
        </div>
      </div>
    </section>
  );
}
